import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface ReportCommentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  commentId: string;
  userId: string | null;
}

const REPORT_REASONS = [
  { value: "spam", label: "Spam or misleading" },
  { value: "harassment", label: "Harassment or bullying" },
  { value: "hate", label: "Hate speech" },
  { value: "inappropriate", label: "Inappropriate content" },
  { value: "other", label: "Other" }
];

const MAX_DETAILS_LENGTH = 1000;

export const ReportCommentDialog = ({ open, onOpenChange, commentId, userId }: ReportCommentDialogProps) => {
  const [selectedReason, setSelectedReason] = useState<string>("");
  const [additionalDetails, setAdditionalDetails] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!userId) {
      toast.error("Please sign in to report comments");
      return;
    }

    if (!selectedReason) {
      toast.error("Please select a reason");
      return;
    }

    if (selectedReason === "other" && additionalDetails.trim().length > MAX_DETAILS_LENGTH) {
      toast.error(`Additional details must be less than ${MAX_DETAILS_LENGTH} characters`);
      return;
    }

    setSubmitting(true);

    const reason = selectedReason === "other" && additionalDetails.trim()
      ? additionalDetails.trim()
      : REPORT_REASONS.find(r => r.value === selectedReason)?.label || selectedReason;

    const { error } = await supabase
      .from("reported_comments")
      .insert({
        comment_id: commentId,
        user_id: userId,
        reason: reason
      });

    setSubmitting(false);

    if (error) {
      toast.error("Failed to submit report");
      console.error(error);
    } else {
      toast.success("Report submitted successfully");
      setSelectedReason("");
      setAdditionalDetails("");
      onOpenChange(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Report Comment</DialogTitle>
          <DialogDescription>
            Help us understand what's wrong with this comment
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <RadioGroup value={selectedReason} onValueChange={setSelectedReason}>
            {REPORT_REASONS.map((reason) => (
              <div key={reason.value} className="flex items-center space-x-2">
                <RadioGroupItem value={reason.value} id={reason.value} />
                <Label htmlFor={reason.value} className="cursor-pointer">
                  {reason.label}
                </Label>
              </div>
            ))}
          </RadioGroup>

          {selectedReason === "other" && (
            <div className="space-y-2">
              <Textarea
                placeholder="Please provide additional details..."
                value={additionalDetails}
                onChange={(e) => setAdditionalDetails(e.target.value)}
                maxLength={MAX_DETAILS_LENGTH}
                className="min-h-[100px]"
              />
              <p className="text-xs text-muted-foreground text-right">
                {additionalDetails.length}/{MAX_DETAILS_LENGTH} characters
              </p>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2">
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={submitting || !selectedReason}
          >
            {submitting ? "Submitting..." : "Submit Report"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
